import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { Button } from "@/components/ui/button";

import {
  Banknote,
  Check,
  Coins,
  Search,
  RotateCcw,
  WalletCards,
  CreditCard,
  X,
  Clock3,
} from "lucide-react";

import { useEffect, useMemo, useState } from "react";

import { normalizeCurrency, removeTrailingToken } from "@/utils";
import TokenIcon from "../shared/token-icon";
import { FiatCurrencyImage } from "../shared/FiatCurrencyImage";
import type {
  MarketFilterSheetProps,
  P2PMarketFilters,
} from "./filter/interface";

type AmountInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  suffix?: string;
  isDark: boolean;
};

const AmountInput = ({
  value,
  onChange,
  placeholder,
  suffix,
  isDark,
}: AmountInputProps) => {
  const mutedText = isDark ? "text-muted-foreground" : "text-gray-500";

  const inputClass = `
    w-full h-14 rounded-xl border px-3.5 pr-16
    text-sm outline-none transition-all
    focus:border-primary
    focus:ring-2 focus:ring-primary/15
    ${
      isDark
        ? "bg-muted/50 border-border text-foreground placeholder:text-muted-foreground"
        : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"
    }
  `;

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputClass}
      />

      {suffix && (
        <span
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold ${mutedText}`}
        >
          {suffix}
        </span>
      )}
    </div>
  );
};

export const MarketFilterSheet = ({
  open,
  onOpenChange,
  type,
  theme,
  paymentMethodFilter,
  searchQuery,
  cryptoCode,
  fiatCurrencyId,
  filters,
  onApplyAmount,
  onApplyPayment,
  onApplySearch,
  onApplyCrypto,
  onApplyFilters,
  onClear,
  tokens,
  currencies,
}: MarketFilterSheetProps) => {
  const [localCrypto, setLocalCrypto] = useState(cryptoCode);
  const [localFiat, setLocalFiat] = useState(fiatCurrencyId);
  const [localMinAmount, setLocalMinAmount] = useState(filters.minAmount ?? "");

  const [localMaxAmount, setLocalMaxAmount] = useState(filters.maxAmount ?? "");

  const [localPayment, setLocalPayment] = useState(paymentMethodFilter);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [paymentMethodSearch, setPaymentMethodSearch] = useState("");

  const [localFilters, setLocalFilters] = useState<P2PMarketFilters>(filters);

  const isDark = theme === "dark";

  const updateFilter = <K extends keyof P2PMarketFilters>(
    key: K,
    value: P2PMarketFilters[K],
  ) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (type === "amount") {
      setLocalMinAmount(filters.minAmount ?? "");
      setLocalMaxAmount(filters.maxAmount ?? "");
    }
  }, [type, filters.minAmount, filters.maxAmount]);

  const togglePaymentMethod = (paymentMethodId: string) => {
    setLocalFilters((prev) => {
      const paymentMethodIds = prev.paymentMethodIds ?? [];
      const exists = paymentMethodIds.includes(paymentMethodId);

      return {
        ...prev,
        paymentMethodIds: exists
          ? paymentMethodIds.filter((id) => id !== paymentMethodId)
          : [...paymentMethodIds, paymentMethodId],
      };
    });
  };

  const selectedFiat = useMemo(
    () => currencies?.find((currency) => currency.id === localFiat),
    [currencies, localFiat],
  );

  const fiatSymbol = selectedFiat?.symbol ?? "";
  const fiatCode = selectedFiat?.iso ?? "FIAT";

  const amountError = useMemo(() => {
    const min = Number(localMinAmount);
    const max = Number(localMaxAmount);

    if (
      localMinAmount.trim() &&
      localMaxAmount.trim() &&
      Number.isFinite(min) &&
      Number.isFinite(max) &&
      min > max
    ) {
      return "Minimum amount cannot be greater than maximum amount.";
    }

    return null;
  }, [localMinAmount, localMaxAmount]);

  useEffect(() => {
    if (!open) return;

    setLocalMinAmount(filters.minAmount ?? "");
    setLocalMaxAmount(filters.maxAmount ?? "");

    setLocalPayment(paymentMethodFilter);
    setLocalSearch(searchQuery);
    setLocalCrypto(cryptoCode);
    setLocalFiat(fiatCurrencyId);
    setPaymentMethodSearch("");

    setLocalFilters({
      ...filters,
      paymentMethodIds: [...(filters.paymentMethodIds ?? [])],
    });
  }, [
    open,
    paymentMethodFilter,
    searchQuery,
    cryptoCode,
    fiatCurrencyId,
    filters.minAmount,
    filters.maxAmount,
    filters.paymentMethodIds,
    filters.verifiedOnly,
    filters.trustedOnly,
    filters.minCompletionRate,
    filters.maxReleaseTime,
    filters.sortBy,
  ]);

  const mutedText = isDark ? "text-muted-foreground" : "text-gray-500";

  const primaryText = isDark ? "text-foreground" : "text-gray-900";

  const inputClass = `
    w-full h-11 rounded-xl border px-3.5
    text-sm outline-none transition-all
    focus:border-primary
    focus:ring-2 focus:ring-primary/15
    ${
      isDark
        ? "bg-muted/50 border-border text-foreground placeholder:text-muted-foreground"
        : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"
    }
  `;

  const labelClass = `
    mb-2 block text-sm font-semibold
    ${primaryText}
  `;

  const sheetSide = type === "amount" ? "bottom" : "top";

  const title = {
    crypto: "Select currency",
    amount: "Trade amount",
    payment: "Payment method",
    search: "Search merchants",
    filter: "Market filters",
  }[type];

  const description = {
    crypto: "Choose the crypto or fiat asset you want to trade.",
    amount: "Find offers that support the amount you want to trade.",
    payment: "Choose a payment method accepted by the merchant.",
    search: "Search merchants by name or payment provider.",
    filter: "Customize the market using asset and fiat currency.",
  }[type];

  const selectedCurrency = useMemo(
    () => currencies?.find((currency) => currency.id === localFiat),
    [currencies, localFiat],
  );

  const paymentMethods = selectedCurrency?.banks ?? [];

  const filteredPaymentMethods = useMemo(() => {
    const query = paymentMethodSearch.trim().toLowerCase();

    if (!query) {
      return paymentMethods;
    }

    return paymentMethods.filter((method) =>
      method.name.toLowerCase().includes(query),
    );
  }, [paymentMethods, paymentMethodSearch]);

  const handleApply = () => {
    switch (type) {
      case "crypto":
        onApplyCrypto(localCrypto || null);
        break;

      case "amount": {
        const min = localMinAmount.trim();
        const max = localMaxAmount.trim();

        if (min && max) {
          const minValue = Number(min);
          const maxValue = Number(max);

          if (
            !Number.isFinite(minValue) ||
            !Number.isFinite(maxValue) ||
            minValue < 0 ||
            maxValue < 0 ||
            minValue > maxValue
          ) {
            return;
          }
        }

        onApplyAmount(min, max);
        break;
      }

      case "payment":
        onApplyPayment(localPayment);
        break;

      case "search":
        onApplySearch(localSearch);
        break;

      case "filter":
        if (amountError) {
          return;
        }

        onApplyFilters({
          ...localFilters,
          cryptoCode: localCrypto || null,
          fiatCurrencyId: localFiat || null,
          minAmount: localFilters.minAmount.trim(),
          maxAmount: localFilters.maxAmount.trim(),
          paymentMethodIds: [...localFilters.paymentMethodIds],
        });
        break;
    }

    onOpenChange(false);
  };
  const handleClear = () => {
    switch (type) {
      case "crypto":
        setLocalCrypto("");
        onApplyCrypto(null);
        break;

      case "amount":
        setLocalMinAmount("");
        setLocalMaxAmount("");
        onApplyAmount("", "");
        break;

      case "payment":
        setLocalPayment("");
        onApplyPayment("");
        break;

      case "search":
        setLocalSearch("");
        onApplySearch("");
        break;

      case "filter":
        setLocalCrypto("");
        setLocalFiat("");
        setPaymentMethodSearch("");

        setLocalFilters({
          cryptoCode: null,
          fiatCurrencyId: null,
          minAmount: "",
          maxAmount: "",
          paymentMethodIds: [],
          verifiedOnly: false,
          trustedOnly: false,
          minCompletionRate: null,
          maxReleaseTime: null,
          sortBy: "BEST_MATCH",
        });

        onClear();
        break;
    }

    onOpenChange(false);
  };

  const optionClass = (active: boolean) => `
  group flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-3 sm:px-3.5 sm:py-3.5 text-left transition-all duration-200 active:scale-[0.99]

  ${
    active
      ? isDark
        ? "border-primary/50 bg-primary/10"
        : "border-primary/40 bg-primary/[0.05]"
      : isDark
        ? "border-border/70 bg-muted/30 hover:border-border hover:bg-muted/60"
        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
  }
`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={sheetSide}
        className={`flex flex-col overflow-hidden border shadow-2xl left-1/2 -translate-x-1/2 w-[calc(100%-1rem)] sm:w-[min(92vw,520px)] md:w-[min(88vw,560px)] lg:w-[min(70vw,600px)] xl:w-[min(60vw,640px)] max-h-[88vh] ${type === "amount" ? "rounded-t-2xl" : "rounded-b-2xl"}${
          isDark
            ? "border-border bg-card text-foreground"
            : "border-gray-200 bg-white text-gray-900"
        }`}
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <SheetHeader
          className={` border-b px-4 pb-4 pt-2 sm:px-5 md:px-6 text-left ${isDark ? "border-border" : "border-gray-200"} `}
        >
          <SheetTitle
            className={`text-base sm:text-lg font-semibold ${primaryText}`}
          >
            {title}
          </SheetTitle>

          <SheetDescription className={mutedText}>
            {description}
          </SheetDescription>
        </SheetHeader>

        {/* ================================================= */}
        {/* CONTENT */}
        {/* ================================================= */}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-5 md:px-6">
          <div className="mx-auto w-full py-4 sm:py-5 md:py-6">
            {/* ================================================= */}
            {/* CRYPTO */}
            {/* ================================================= */}

            {type === "crypto" && (
              <div className="space-y-2">
                {tokens?.map((token) => {
                  const active = localCrypto === token.code;

                  return (
                    <button
                      key={token.id}
                      type="button"
                      onClick={() => setLocalCrypto(token.code)}
                      className={optionClass(active)}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <TokenIcon
                          token={token}
                          id={`market-crypto-${token.id}`}
                          size="md"
                        />

                        <div className="min-w-0 text-left">
                          <p
                            className={`truncate text-sm font-semibold ${primaryText}`}
                          >
                            {normalizeCurrency(token.code)}
                          </p>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            {removeTrailingToken(token.name)}
                          </p>
                        </div>
                      </div>

                      {active && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* ================================================= */}
            {/* AMOUNT */}
            {/* ================================================= */}

            {type === "amount" && (
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Minimum fiat amount</label>

                  <div className="relative mt-2">
                    <input
                      type="number"
                      min="0"
                      value={localMinAmount}
                      onChange={(e) => setLocalMinAmount(e.target.value)}
                      placeholder="e.g. 50000"
                      className={`${inputClass} pr-16 h-14`}
                      autoFocus
                    />

                    <span
                      className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold ${mutedText}`}
                    >
                      {selectedFiat?.iso ?? "FIAT"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Maximum fiat amount</label>

                  <div className="relative mt-2">
                    <input
                      type="number"
                      min="0"
                      value={localMaxAmount}
                      onChange={(e) => setLocalMaxAmount(e.target.value)}
                      placeholder="e.g. 700000"
                      className={`${inputClass} pr-16 h-14`}
                    />

                    <span
                      className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold ${mutedText}`}
                    >
                      {selectedFiat?.iso ?? "FIAT"}
                    </span>
                  </div>
                </div>

                <p className={`text-xs leading-5 ${mutedText}`}>
                  Only offers whose fiat trading limits overlap this amount
                  range will be shown.
                </p>
              </div>
            )}

            {/* ================================================= */}
            {/* PAYMENT */}
            {/* ================================================= */}

            {type === "payment" && (
              <div>
                <label className={labelClass}>Payment method</label>

                <div className="space-y-2">
                  {/* All payment methods */}

                  <button
                    type="button"
                    onClick={() => setLocalPayment("")}
                    className={optionClass(localPayment === "")}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${isDark ? "bg-muted" : "bg-gray-100"}`}
                      >
                        <WalletCards className="size-5" />
                      </div>

                      <div>
                        <p className={`text-sm font-semibold ${primaryText}`}>
                          All payment methods
                        </p>

                        <p className={`mt-0.5 text-xs ${mutedText}`}>
                          Show every available option
                        </p>
                      </div>
                    </div>

                    {localPayment === "" && (
                      <Check className="size-5 text-primary" />
                    )}
                  </button>

                  {/* Banks */}

                  {selectedCurrency?.banks?.map((bank) => {
                    const active = localPayment === bank.id;

                    return (
                      <button
                        key={bank.id}
                        type="button"
                        onClick={() => setLocalPayment(bank.id)}
                        className={optionClass(active)}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex size-10 items-center justify-center rounded-xl
                              ${isDark ? "bg-muted" : "bg-gray-100"}`}
                          >
                            <Banknote className="size-5" />
                          </div>

                          <div className="text-left">
                            <p
                              className={`text-sm font-semibold ${primaryText}`}
                            >
                              {bank.name}
                            </p>

                            <p className={`mt-0.5 text-xs ${mutedText}`}>
                              Bank transfer
                            </p>
                          </div>
                        </div>

                        {active && <Check className="size-5 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ================================================= */}
            {/* SEARCH */}
            {/* ================================================= */}

            {type === "search" && (
              <div>
                <label className={labelClass}>Merchant</label>

                <div className="relative">
                  <Search
                    className={`absolute left-3.5 top-1/2 size-4 -translate-y-1/2 ${mutedText}`}
                  />

                  <input
                    type="search"
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    placeholder="Search merchants or banks..."
                    className={`${inputClass} pl-10 h-14`}
                    autoFocus
                  />
                </div>

                <p className={`mt-2 text-xs ${mutedText}`}>
                  Search by merchant name or payment provider.
                </p>
              </div>
            )}

            {/* ================================================= */}
            {/* FILTER */}
            {/* ================================================= */}

            {type === "filter" && (
              <div className="space-y-6">
                {/* Crypto */}

                <div>
                  <label className={labelClass}>Cryptocurrency</label>

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setLocalCrypto("")}
                      className={optionClass(!localCrypto)}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex size-9 items-center justify-center rounded-lg ${isDark ? "bg-muted" : "bg-gray-100"}`}
                        >
                          <Coins className="size-4" />
                        </div>

                        <span className={`text-sm font-medium ${primaryText}`}>
                          All cryptocurrencies
                        </span>
                      </div>

                      {!localCrypto && (
                        <Check className="size-4 text-primary" />
                      )}
                    </button>

                    {tokens?.map((token) => {
                      const active = localCrypto === token.code;

                      return (
                        <button
                          key={token.id}
                          type="button"
                          onClick={() => setLocalCrypto(token.code)}
                          className={optionClass(active)}
                        >
                          <div className="flex items-center gap-3">
                            <TokenIcon
                              token={token}
                              id={`market-filter-crypto-${token.id}`}
                              size="sm"
                            />

                            <span
                              className={`text-sm font-medium ${primaryText}`}
                            >
                              {normalizeCurrency(token.code)}
                            </span>
                          </div>

                          {active && <Check className="size-4 text-primary" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Fiat */}
                <div>
                  <label className={labelClass}>Fiat currency</label>

                  <div className="space-y-2">
                    {/* All currencies */}
                    <button
                      type="button"
                      onClick={() => setLocalFiat("")}
                      className={optionClass(!localFiat)}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex size-9 shrink-0 items-center justify-center rounded-full ${
                            isDark ? "bg-muted" : "bg-gray-100"
                          }`}
                        >
                          <Banknote
                            className={`size-4 ${
                              isDark ? "text-muted-foreground" : "text-gray-500"
                            }`}
                          />
                        </div>

                        <div className="min-w-0 text-left">
                          <p className={`text-sm font-semibold ${primaryText}`}>
                            All currencies
                          </p>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            Show all supported fiat currencies
                          </p>
                        </div>
                      </div>

                      {!localFiat && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>

                    {/* Currencies */}
                    {currencies?.map((currency) => {
                      const active = localFiat === currency.id;

                      return (
                        <button
                          key={currency.id}
                          type="button"
                          onClick={() => setLocalFiat(currency.id)}
                          className={optionClass(active)}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <FiatCurrencyImage
                              iso={currency.iso2}
                              name={currency.name}
                              className={`size-9 shrink-0 rounded-full border ${
                                isDark ? "border-border" : "border-gray-200"
                              }`}
                            />

                            <div className="min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <p
                                  className={`truncate text-sm font-semibold ${primaryText}`}
                                >
                                  {currency.name}
                                </p>

                                <span
                                  className={`shrink-0 text-xs font-medium ${mutedText}`}
                                >
                                  {currency.iso}
                                </span>
                              </div>

                              <p className={`mt-0.5 text-xs ${mutedText}`}>
                                {currency.symbol} · Fiat currency
                              </p>
                            </div>
                          </div>

                          {active && (
                            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                              <Check className="size-3.5 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Trade amount */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className={labelClass}>Trade amount</label>

                    {selectedFiat && (
                      <span className={`text-xs font-medium ${mutedText}`}>
                        {fiatCode}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className={`mb-1.5 text-xs ${mutedText}`}>Minimum</p>
                      <AmountInput
                        value={localFilters.minAmount}
                        onChange={(value) => updateFilter("minAmount", value)}
                        placeholder="0"
                        suffix={fiatSymbol}
                        isDark={isDark}
                      />
                    </div>

                    <div>
                      <p className={`mb-1.5 text-xs ${mutedText}`}>Maximum</p>

                      <AmountInput
                        value={localFilters.maxAmount}
                        onChange={(value) => updateFilter("maxAmount", value)}
                        placeholder="No limit"
                        suffix={fiatSymbol}
                        isDark={isDark}
                      />
                    </div>
                  </div>

                  <p className={`mt-2 text-xs leading-5 ${mutedText}`}>
                    Only offers that support this trade amount will be shown.
                  </p>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {["50000", "100000", "500000", "1000000"].map((value) => {
                      const formatted = new Intl.NumberFormat("en-NG", {
                        notation: "compact",
                        maximumFractionDigits: 1,
                      }).format(Number(value));

                      const active =
                        localFilters.minAmount === value &&
                        localFilters.maxAmount === value;

                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            updateFilter("minAmount", value);
                            updateFilter("maxAmount", value);
                          }}
                          className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-primary/40 bg-primary/10 text-primary" : isDark ? "border-border bg-muted/30 text-muted-foreground hover:bg-muted" : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                        >
                          {fiatSymbol}
                          {formatted}
                        </button>
                      );
                    })}
                  </div>

                  {amountError && (
                    <p className="mt-2 text-xs font-medium text-destructive">
                      {amountError}
                    </p>
                  )}
                </div>

                {/* ================================================= */}
                {/* PAYMENT METHODS */}
                {/* ================================================= */}

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className={labelClass}>Payment method</label>

                    {localFilters?.paymentMethodIds?.length > 0 && (
                      <span className="text-xs font-medium text-primary">
                        {localFilters?.paymentMethodIds?.length} selected
                      </span>
                    )}
                  </div>

                  <p className={`mb-3 text-xs leading-5 ${mutedText}`}>
                    Select one or more payment methods accepted by the merchant.
                  </p>

                  {/* Search payment methods */}

                  {paymentMethods?.length > 5 && (
                    <div className="relative mb-3">
                      <Search
                        className={`absolute left-3.5 top-1/2 size-4 -translate-y-1/2 ${mutedText}`}
                      />

                      <input
                        type="search"
                        value={paymentMethodSearch}
                        onChange={(e) => setPaymentMethodSearch(e.target.value)}
                        placeholder="Search payment methods..."
                        className={`${inputClass} h-11 pl-10`}
                      />

                      {paymentMethodSearch && (
                        <button
                          type="button"
                          onClick={() => setPaymentMethodSearch("")}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 ${
                            isDark
                              ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                              : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          }`}
                          aria-label="Clear payment method search"
                        >
                          <X className="size-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* All payment methods */}

                  <button
                    type="button"
                    onClick={() => updateFilter("paymentMethodIds", [])}
                    className={optionClass(
                      localFilters?.paymentMethodIds?.length === 0,
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                          isDark ? "bg-muted" : "bg-gray-100"
                        }`}
                      >
                        <WalletCards
                          className={`size-4 ${
                            isDark ? "text-muted-foreground" : "text-gray-500"
                          }`}
                        />
                      </div>

                      <div className="min-w-0 text-left">
                        <p className={`text-sm font-semibold ${primaryText}`}>
                          All payment methods
                        </p>

                        <p className={`mt-0.5 text-xs ${mutedText}`}>
                          Show offers from every supported payment method
                        </p>
                      </div>
                    </div>

                    {localFilters?.paymentMethodIds?.length === 0 && (
                      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                        <Check className="size-3.5 text-white" />
                      </div>
                    )}
                  </button>

                  {/* Payment methods */}

                  <div className="mt-2 space-y-2">
                    {filteredPaymentMethods?.map((method) => {
                      const active = localFilters?.paymentMethodIds?.includes(
                        method.id,
                      );

                      return (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => togglePaymentMethod(method.id)}
                          className={optionClass(active)}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <div
                              className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                                isDark ? "bg-muted" : "bg-gray-100"
                              }`}
                            >
                              <CreditCard
                                className={`size-4 ${
                                  active
                                    ? "text-primary"
                                    : isDark
                                      ? "text-muted-foreground"
                                      : "text-gray-500"
                                }`}
                              />
                            </div>

                            <div className="min-w-0 text-left">
                              <p
                                className={`truncate text-sm font-semibold ${primaryText}`}
                              >
                                {method.name}
                              </p>

                              <p className={`mt-0.5 text-xs ${mutedText}`}>
                                {selectedFiat?.name
                                  ? `${selectedFiat.name} payment method`
                                  : "Payment method"}
                              </p>
                            </div>
                          </div>

                          {active && (
                            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                              <Check className="size-3.5 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Empty state */}

                  {paymentMethods?.length === 0 && (
                    <div
                      className={`mt-2 rounded-xl border px-4 py-6 text-center ${
                        isDark
                          ? "border-border bg-muted/30"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <WalletCards
                        className={`mx-auto mb-2 size-5 ${mutedText}`}
                      />

                      <p className={`text-sm font-medium ${primaryText}`}>
                        No payment methods available
                      </p>

                      <p className={`mt-1 text-xs ${mutedText}`}>
                        Select a fiat currency to see its supported payment
                        methods.
                      </p>
                    </div>
                  )}

                  {paymentMethods?.length > 0 &&
                    filteredPaymentMethods?.length === 0 && (
                      <div
                        className={`mt-2 rounded-xl border px-4 py-6 text-center ${
                          isDark
                            ? "border-border bg-muted/30"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <Search
                          className={`mx-auto mb-2 size-5 ${mutedText}`}
                        />

                        <p className={`text-sm font-medium ${primaryText}`}>
                          No payment methods found
                        </p>

                        <p className={`mt-1 text-xs ${mutedText}`}>
                          Try a different payment method search.
                        </p>
                      </div>
                    )}
                </div>

                {/* MERCHANT QUALITY */}
                <div>
                  <div className="mb-2">
                    <label className={labelClass}>Merchant quality</label>

                    <p className={`text-xs leading-5 ${mutedText}`}>
                      Filter offers based on merchant reliability and trading
                      history.
                    </p>
                  </div>

                  <div className="space-y-2">
                    {/* ============================================= */}
                    {/* VERIFIED MERCHANTS */}
                    {/* ============================================= */}

                    <button
                      type="button"
                      onClick={() =>
                        updateFilter(
                          "verifiedOnly",
                          !localFilters?.verifiedOnly,
                        )
                      }
                      className={optionClass(localFilters?.verifiedOnly)}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                            localFilters?.verifiedOnly
                              ? "bg-primary/10 text-primary"
                              : isDark
                                ? "bg-muted text-muted-foreground"
                                : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          <Check className="size-4" />
                        </div>

                        <div className="min-w-0 text-left">
                          <div className="flex items-center gap-2">
                            <p
                              className={`text-sm font-semibold ${primaryText}`}
                            >
                              Verified merchants
                            </p>

                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                localFilters?.verifiedOnly
                                  ? "bg-primary/10 text-primary"
                                  : isDark
                                    ? "bg-muted text-muted-foreground"
                                    : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              Verified
                            </span>
                          </div>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            Only show merchants who have completed verification.
                          </p>
                        </div>
                      </div>

                      {localFilters?.verifiedOnly && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>

                    {/* ============================================= */}
                    {/* TRUSTED MERCHANTS */}
                    {/* ============================================= */}

                    <button
                      type="button"
                      onClick={() =>
                        updateFilter("trustedOnly", !localFilters?.trustedOnly)
                      }
                      className={optionClass(localFilters?.trustedOnly)}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                            localFilters?.trustedOnly
                              ? "bg-primary/10 text-primary"
                              : isDark
                                ? "bg-muted text-muted-foreground"
                                : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          <WalletCards className="size-4" />
                        </div>

                        <div className="min-w-0 text-left">
                          <div className="flex items-center gap-2">
                            <p
                              className={`text-sm font-semibold ${primaryText}`}
                            >
                              Trusted merchants
                            </p>

                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                localFilters?.trustedOnly
                                  ? "bg-primary/10 text-primary"
                                  : isDark
                                    ? "bg-muted text-muted-foreground"
                                    : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              Trusted
                            </span>
                          </div>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            Only show merchants with a strong trading history.
                          </p>
                        </div>
                      </div>

                      {localFilters?.trustedOnly && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>
                  </div>

                  {/* Minimum completion rate */}

                  <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between">
                      <label className={labelClass}>
                        Minimum completion rate
                      </label>

                      {localFilters?.minCompletionRate !== null && (
                        <span className="text-xs font-semibold text-primary">
                          {localFilters?.minCompletionRate}%
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {[70, 80, 90, 95].map((rate) => {
                        const active = localFilters?.minCompletionRate === rate;

                        return (
                          <button
                            key={rate}
                            type="button"
                            onClick={() =>
                              updateFilter(
                                "minCompletionRate",
                                active ? null : rate,
                              )
                            }
                            className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-all ${
                              active
                                ? isDark
                                  ? "border-primary/50 bg-primary/10 text-primary"
                                  : "border-primary/40 bg-primary/[0.05] text-primary"
                                : isDark
                                  ? "border-border/70 bg-muted/30 text-muted-foreground hover:border-border hover:bg-muted/60"
                                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {rate}%
                          </button>
                        );
                      })}
                    </div>

                    <p className={`mt-2 text-xs leading-5 ${mutedText}`}>
                      Only show merchants whose completed-order rate is at least
                      the selected percentage.
                    </p>
                  </div>
                </div>

                {/* Release time */}
                <div>
                  <div className="mb-2">
                    <label className={labelClass}>Release time</label>

                    <p className={`text-xs leading-5 ${mutedText}`}>
                      Choose merchants based on how quickly they release
                      cryptocurrency after payment.
                    </p>
                  </div>

                  <div className="space-y-2">
                    {/* Any release time */}

                    <button
                      type="button"
                      onClick={() => updateFilter("maxReleaseTime", null)}
                      className={optionClass(
                        localFilters?.maxReleaseTime === null,
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                            localFilters?.maxReleaseTime === null
                              ? "bg-primary/10"
                              : isDark
                                ? "bg-muted"
                                : "bg-gray-100"
                          }`}
                        >
                          <Clock3
                            className={`size-4 ${
                              localFilters?.maxReleaseTime === null
                                ? "text-primary"
                                : mutedText
                            }`}
                          />
                        </div>

                        <div className="min-w-0 text-left">
                          <p className={`text-sm font-semibold ${primaryText}`}>
                            Any release time
                          </p>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            Show all available release times
                          </p>
                        </div>
                      </div>

                      {localFilters?.maxReleaseTime === null && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>

                    {/* 15 minutes */}

                    <button
                      type="button"
                      onClick={() => updateFilter("maxReleaseTime", 15)}
                      className={optionClass(
                        localFilters?.maxReleaseTime === 15,
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                            localFilters?.maxReleaseTime === 15
                              ? "bg-primary/10"
                              : isDark
                                ? "bg-muted"
                                : "bg-gray-100"
                          }`}
                        >
                          <Clock3
                            className={`size-4 ${
                              localFilters?.maxReleaseTime === 15
                                ? "text-primary"
                                : mutedText
                            }`}
                          />
                        </div>

                        <div className="min-w-0 text-left">
                          <p className={`text-sm font-semibold ${primaryText}`}>
                            Within 15 minutes
                          </p>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            Only show fast-release merchants
                          </p>
                        </div>
                      </div>

                      {localFilters?.maxReleaseTime === 15 && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>

                    {/* 30 minutes */}

                    <button
                      type="button"
                      onClick={() => updateFilter("maxReleaseTime", 30)}
                      className={optionClass(
                        localFilters?.maxReleaseTime === 30,
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                            localFilters?.maxReleaseTime === 30
                              ? "bg-primary/10"
                              : isDark
                                ? "bg-muted"
                                : "bg-gray-100"
                          }`}
                        >
                          <Clock3
                            className={`size-4 ${
                              localFilters?.maxReleaseTime === 30
                                ? "text-primary"
                                : mutedText
                            }`}
                          />
                        </div>

                        <div className="min-w-0 text-left">
                          <p className={`text-sm font-semibold ${primaryText}`}>
                            Within 30 minutes
                          </p>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            Only show merchants with a release time of 30
                            minutes or less
                          </p>
                        </div>
                      </div>

                      {localFilters?.maxReleaseTime === 30 && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>

                    {/* 1 hour */}

                    <button
                      type="button"
                      onClick={() => updateFilter("maxReleaseTime", 60)}
                      className={optionClass(
                        localFilters?.maxReleaseTime === 60,
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                            localFilters?.maxReleaseTime === 60
                              ? "bg-primary/10"
                              : isDark
                                ? "bg-muted"
                                : "bg-gray-100"
                          }`}
                        >
                          <Clock3
                            className={`size-4 ${
                              localFilters?.maxReleaseTime === 60
                                ? "text-primary"
                                : mutedText
                            }`}
                          />
                        </div>

                        <div className="min-w-0 text-left">
                          <p className={`text-sm font-semibold ${primaryText}`}>
                            Within 1 hour
                          </p>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            Only show merchants with a release time of 1 hour or
                            less
                          </p>
                        </div>
                      </div>

                      {localFilters?.maxReleaseTime === 60 && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>

                    {/* 2 hours */}

                    <button
                      type="button"
                      onClick={() => updateFilter("maxReleaseTime", 120)}
                      className={optionClass(
                        localFilters?.maxReleaseTime === 120,
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${
                            localFilters?.maxReleaseTime === 120
                              ? "bg-primary/10"
                              : isDark
                                ? "bg-muted"
                                : "bg-gray-100"
                          }`}
                        >
                          <Clock3
                            className={`size-4 ${
                              localFilters?.maxReleaseTime === 120
                                ? "text-primary"
                                : mutedText
                            }`}
                          />
                        </div>

                        <div className="min-w-0 text-left">
                          <p className={`text-sm font-semibold ${primaryText}`}>
                            Within 2 hours
                          </p>

                          <p className={`mt-0.5 text-xs ${mutedText}`}>
                            Only show merchants with a release time of 2 hours
                            or less
                          </p>
                        </div>
                      </div>

                      {localFilters?.maxReleaseTime === 120 && (
                        <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary">
                          <Check className="size-3.5 text-white" />
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {/* ============================================= */}
                {/* SELECTED COUNT */}
                {/* ============================================= */}

                {localFilters?.paymentMethodIds?.length > 0 && (
                  <p className={`mt-2 text-xs ${mutedText}`}>
                    {localFilters?.paymentMethodIds?.length} payment{" "}
                    {localFilters?.paymentMethodIds?.length === 1
                      ? "method"
                      : "methods"}{" "}
                    selected
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <SheetFooter
          className={` shrink-0 border-t px-4 py-3 sm:px-5 sm:py-4 md:px-6 ${isDark ? "border-border bg-card" : "border-gray-200 bg-white"}`}
        >
          <div className="mx-auto flex w-full gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClear}
              className={` h-11 sm:h-12 flex-1 gap-2 rounded-xl text-sm ${
                isDark
                  ? "border-border hover:bg-muted"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <RotateCcw className="size-4" />
              Clear
            </Button>

            <Button
              type="button"
              disabled={!!amountError}
              onClick={handleApply}
              className="h-11 sm:h-14 flex-1 rounded-xl text-sm font-semibold shadow-sm"
            >
              Apply filters
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
